import { debounce, throttle } from 'lodash-es';
import { RefObject, useLayoutEffect, useMemo } from 'react';
import { useRafState } from 'react-use';
import { SafeAny } from '../types';

interface ElementPosition {
  x: number;
  y: number;
}
type ElementPositionOnScroll = ElementPosition & { direction: 'up' | 'down' };

type ElementPositionOptionBase = {
  throttle?: boolean;
  debounce?: boolean;
  delay?: number;
};
type ElementPositionOptionSpecific =
  | { throttle: true; delay: number }
  | { debounce: true; delay: number };
type ElementPositionOption = ElementPositionOptionBase &
  (Record<string, never> | ElementPositionOptionSpecific);

function isString(value: SafeAny): value is string {
  return typeof value === 'string';
}

export function useElementPosition(
  idOrRef: string | RefObject<HTMLElement>,
  option?: ElementPositionOption
): ElementPositionOnScroll | null {
  const [position, setElementPosition] = useRafState<ElementPosition | null>(
    null
  );
  const [prevScrollY, setPrevScrollY] = useRafState(window.scrollY);
  const [direction, setDirection] = useRafState<'up' | 'down'>('down');

  // make the handler stable to avoid re-render on every scroll
  const handleElementPosition = useMemo(() => {
    let domNode: HTMLElement | null = null;
    const handle = () => {
      let node: HTMLElement | null;
      if (isString(idOrRef)) {
        if (!domNode) domNode = document.getElementById(idOrRef);
        node = domNode;
      } else {
        node = idOrRef.current;
      }

      if (!node) return;
      const { left, top } = node.getBoundingClientRect();
      setElementPosition((state) =>
        left === state?.x && top === state?.y ? state : { x: left, y: top }
      );
      if (window.scrollY > prevScrollY) {
        setDirection('down');
      } else if (window.scrollY < prevScrollY) {
        setDirection('up');
      }

      setPrevScrollY(window.scrollY);
    };

    if (option?.throttle) return throttle(handle, option.delay);
    if (option?.debounce) return debounce(handle, option.delay);
    return handle;
  }, [
    option?.throttle,
    option?.debounce,
    option?.delay,
    idOrRef,
    prevScrollY,
    setDirection,
    setPrevScrollY,
    setElementPosition,
  ]);

  useLayoutEffect(() => {
    handleElementPosition();
    window.addEventListener('scroll', handleElementPosition);
    return () => window.removeEventListener('scroll', handleElementPosition);
  }, [handleElementPosition]);

  return position ? { ...position, direction } : null;
}

export default useElementPosition;
